import { computed } from 'mobx';
import Model from 'core/model/Model';
import { protoTimestampToIsoString } from 'core/util/dateUtils';
import $alerting from 'app/stores/alerting/$alerting';
import $notifications from 'app/stores/notifications/$notifications';

class InvestigationConfigModel extends Model {
  get id() {
    // Return the actual rule_id value for this model
    // This is used by Collection to populate modelById map
    if (this.has('rule_id')) {
      return this.get('rule_id');
    }
    return this.optimisticId;
  }

  get urlRoot() {
    return '/api/ui/investigation-config';
  }

  get(attribute) {
    // Special handling for id - return rule_id value
    if (attribute === 'id') {
      return this.id;
    }

    // For all other attributes, use base implementation
    return super.get(attribute);
  }

  @computed
  get isNew() {
    return !this.has('rule_id') || (!this.get('rule_id') && this.get('rule_id') !== 0);
  }

  @computed
  get lastModified() {
    return protoTimestampToIsoString(this.get('updated_at'));
  }

  /**
   * Get policy data
   * Policy object comes from the backend response
   * @returns {Object|undefined} Policy object with { id, rule_id, name, application, ... }
   */
  @computed
  get policy() {
    return this.get('policy');
  }

  deserialize(data) {
    // If response has a 'config' wrapper, extract it
    let config = data;
    if (data && typeof data === 'object' && 'config' in data) {
      config = data.config;
    }

    // Transform API data to form-friendly format
    if (config && typeof config === 'object') {
      // Extract runbook IDs from runbooks array for form field
      // Use the API parameter name runbook_ids for consistency
      if (config.runbooks && Array.isArray(config.runbooks)) {
        config.runbook_ids = config.runbooks.map((r) => String(r.runbook_id));
        // Remove the runbooks array - we only need the IDs for the form
        delete config.runbooks;
      } else {
        config.runbook_ids = [];
      }

      const ruleId = config.rule_id ? String(config.rule_id) : undefined;

      // Derive notification_channel_ids from notifications store when available
      if (ruleId) {
        config.notification_channel_ids = $notifications
          .findNotificationsByAiInvestigationRule(ruleId)
          .map((ch) => ch.id);
      } else if (!config.notification_channel_ids) {
        config.notification_channel_ids = [];
      }

      // Derive policy object from alerting store when available
      if (ruleId) {
        const policyModel = $alerting.policyCollection.getPolicyByRuleId(ruleId);
        if (policyModel) {
          config.policy = {
            id: policyModel.id,
            rule_id: ruleId,
            name: policyModel.get('name'),
            application: policyModel.get('application')
          };
        }
      } else if (!config.policy) {
        config.policy = null;
      }
    }

    return config;
  }
}

export default InvestigationConfigModel;
